import { useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import { Dashboard2ColumnType } from '@/features/Dashboard/types/constants';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import useDashboard2Store from '@/stores/Dashboard2Store';
import { usePopup } from '@/stores/pure/PopupStore';

import { trimTrailingEmptyQuillBlocks } from './DashboardCardContent/quill-html';
import SimpleEditor from './DashboardCardContent/SimpleEditor';
import { DashboardTextContentStyle } from './styles/TextContentStyle';

type Props = {
  dashboardId: string;
  length?: number;
};

const ControlledCreateTextArea = (props: Props) => {
  const color = useColor();
  const queryClient = useQueryClient();
  const setPopup = usePopup();
  const textAreaState = useDashboard2Store((state) => state.textAreaState);
  const setTextAreaState = useDashboard2Store((state) => state.setTextArea);
  const { setEditingState, clearEditingState } = useDashboard2Store(
    (state) => ({
      setEditingState: state.setEditingState,
      clearEditingState: state.clearEditingState,
    }),
  );

  const onTitleChange = (value: string) => {
    const isFirstEdit = !textAreaState.status;
    setTextAreaState({
      status: true,
      text: value,
    });

    // Set editing state only on first edit
    if (isFirstEdit) {
      setEditingState(true);
    }
  };

  const onCancel = () => {
    setTextAreaState({
      status: false,
      text: '',
    });
    clearEditingState();
  };

  const refetchDashboard = () => {
    queryClient.invalidateQueries({ queryKey: ['dashboard2', props.dashboardId] });
  };

  const createDashboardRowSection = async () => {
    const nextText = trimTrailingEmptyQuillBlocks(textAreaState.text);

    if (nextText) {
      try {
        await service.dashboard2.addDashboardRow(props.dashboardId, {
          // 실제 row index 로 변경
          pos: props.length || 0,
          columns: [
            {
              text: nextText,
              type: Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX,
            },
          ],
          // title,
          // type: DashboardRowType.DASHBOARD_ROW_SECTION,
        });

        // reset Form first to clear editing state
        setTextAreaState({
          status: false,
          text: '',
        });
        clearEditingState();

        // then refetch dashboard data
        refetchDashboard();
      } catch (e) {
        setPopup({
          popupTheme: 'negative',
          title: 'Error',
          message: 'Failed to create the section. Please try again later.',
          type: 'alert',
        });
      }
    } else {
      // reset Form
      setTextAreaState({
        status: false,
        text: '',
      });
      clearEditingState();
    }
  };

  if (textAreaState.status) {
    return (
      <EditorWrapperStyle style={{ width: '100%' }} g={8}>
        <SimpleEditor
          value={textAreaState.text}
          onChange={onTitleChange}
          style={{
            padding: '8px 24px',
          }}
          wrapperStyle={{
            backgroundColor: color.gray200,

            borderRadius: 16,
            height: '100%',
            maxHeight: 600,
            overflow: 'auto',
            flex: 1,
          }}
          textStyleCss={DashboardTextContentStyle}
        />
        <Flex j="space-between">
          <Flex g={8} j="flex-end">
            <Button variant="secondary" onClick={onCancel}>
              cancel
            </Button>
            <Button onClick={createDashboardRowSection}>Done</Button>
          </Flex>
        </Flex>
      </EditorWrapperStyle>
    );
  }
  return null;
};

const EditorWrapperStyle = styled(VFlex)`
  flex: 1;
  flex-shrink: 1;
  min-height: 140px;
  overflow: auto;
  padding: 0px 12px;
  padding-bottom: 24px;
`;

export default ControlledCreateTextArea;
